<template>
    <UILoader v-if="isDeliveryPriceLoading || isAuctionsLoading" class="modalable" />

    <ConfirmActionModal v-else :on-confirm="onConfirm">
        <div class="entity-confirm-publish entity-confirm-action">
            <div class="entity-confirm-publish__title">
                Вы уверены, что хотите создать аукцион по следующим автомобилям?
            </div>

            <div v-for="car in cars" class="entity">
                <div class="entity__list">
                    <div class="entity__list-item">
                        <div class="entity__list-label">VIN:</div>
                        <div class="entity__list-value">{{ car.vin || '-' }}</div>
                    </div>

                    <div class="entity__list-item">
                        <div class="entity__list-label">Марка:</div>
                        <div class="entity__list-value">{{ car.brand.name || '-' }}</div>
                    </div>

                    <div class="entity__list-item">
                        <div class="entity__list-label">Модель:</div>
                        <div class="entity__list-value">{{ car.model.name || '-' }}</div>
                    </div>

                    <div class="entity__list-item">
                        <div class="entity__list-label">Дата начала:</div>
                        <div class="entity__list-value">{{ startDate ? formatFromUTC(startDate, { toFormat: 'dd.LL.yyyy HH:mm' }) : '-' }}</div>
                    </div>

                    <div class="entity__list-item">
                        <div class="entity__list-label">Дата окончания:</div>
                        <div class="entity__list-value">{{ endDate ? formatFromUTC(endDate, { toFormat: 'dd.LL.yyyy HH:mm' }) : '-' }}</div>
                    </div>

                    <div class="entity__list-item">
                        <div class="entity__list-label">Стартовая цена:</div>
                        <div class="entity__list-value">{{ numberFormat(car.start_price) }} ₽</div>
                    </div>

                    <div v-if="!isCarDeliveryPriceExists(car)" class="entity__list-item entity__list-item-important">
                        <div class="entity__list-label">Тариф на доставку:</div>
                        <div class="entity__list-value">отсутствует</div>
                    </div>

                    <div v-if="getAuctionsCountByCar(car) > 0" class="entity__list-item entity__list-item-important">
                        <div class="entity__list-label">Автомобиль участвовал в аукционах за последний месяц:</div>
                        <div class="entity__list-value">{{ getAuctionsCountByCar(car) }} раз</div>
                    </div>
                </div>
            </div>
        </div>
    </ConfirmActionModal>
</template>

<script lang="ts" setup>
    import { onMounted, ref } from 'vue';

    import { AuctionCarMonthCounter } from '@/types/auction.type.ts';
    import { Car } from '@/types/car.type.ts';
    import { ModelDeliveryPriceStatus } from '@/types/model/model-delivery-price-status.type.ts';

    import useAuctionRepository from '@/repositories/auction/useAuctionRepository.ts';
    import useLoading from '@/utils/useLoading.ts';
    import useModelRepository from '@/repositories/model/useModelRepository.ts';

    import { formatFromUTC } from '@/utils/DateTimeFormatter.ts';
    import { numberFormat } from '@/utils/NumberFormatter.ts';

    import UILoader from '@/components/ui/UILoader/UILoader.vue';

    import ConfirmActionModal from '@/components/modals/ConfirmActionModal/ConfirmActionModal.vue';

    const { isLoading: isDeliveryPriceLoading, setLoading: setDeliveryPriceLoading } = useLoading();
    const { isLoading: isAuctionsLoading, setLoading: setAuctionsLoading } = useLoading();
    const { getModelsDeliveryPricesStatuses } = useModelRepository();
    const { getAuctionCountByCarsIdsOnCurrentMonth } = useAuctionRepository();

    const { startDate, endDate, cars } = defineProps<{
        startDate: string,
        endDate: string,
        cars: Car[],
        onConfirm: Function,
    }>();

    const modelsDeliveryPricesStatuses = ref<ModelDeliveryPriceStatus[]>([]);
    const auctionsCarsMonthCounters = ref<AuctionCarMonthCounter[]>([]);

    function isCarDeliveryPriceExists(car: Car): boolean {
        return modelsDeliveryPricesStatuses.value.filter(
            (status: ModelDeliveryPriceStatus) => status.exist_delivery && status.model_id === car.model.id,
        ).length > 0;
    }

    function getAuctionsCountByCar(car: Car): number {
        return auctionsCarsMonthCounters.value.find(
            (counter: AuctionCarMonthCounter) => counter.car_id === car.id,
        )?.count ?? 0;
    }

    function fetchModelsDeliveryPricesStatuses(): void {
        setDeliveryPriceLoading(true);

        const modelsIds: number[] = cars.map((car: Car) => car.model.id);
        getModelsDeliveryPricesStatuses(modelsIds)
            .then((statuses: ModelDeliveryPriceStatus[]) => {
                modelsDeliveryPricesStatuses.value = statuses;
            })
            .finally(() => setDeliveryPriceLoading(false));
    }

    function fetchAuctionsCounts(): void {
        setAuctionsLoading(true);

        const carIds: number[] = cars.map((car: Car) => car.id);
        getAuctionCountByCarsIdsOnCurrentMonth(carIds)
            .then((counters: AuctionCarMonthCounter[]) => {
                auctionsCarsMonthCounters.value = counters;
            })
            .finally(() => setAuctionsLoading(false));
    }

    onMounted(() => {
        fetchModelsDeliveryPricesStatuses();
        fetchAuctionsCounts();
    });
</script>

<style scoped lang="scss">
    @use 'AuctionConfirmPublish' as *;
</style>
